"use client";

import { useTranslations } from "next-intl";
import { CalendarPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SITE_URL } from "@/lib/site-url";
import type { PublicContestDetail } from "../types";

/** `20261024T153000Z`, the iCalendar and Google Calendar UTC form. */
function calendarStamp(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/** RFC 5545 text: a backslash, a semicolon, a comma and a newline are escaped. */
function icsText(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

/**
 * The contest as one calendar event: the title, the window and the page's
 * address, nothing about the reader (`EX-CPG02`). Exported for its test.
 */
export function contestIcs(input: { slug: string; title: string; startAt: string; endAt: string; url: string; now: Date }): string {
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Progsity//Contests//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${input.slug}@progsity.io`,
    `DTSTAMP:${calendarStamp(input.now.toISOString())}`,
    `DTSTART:${calendarStamp(input.startAt)}`,
    `DTEND:${calendarStamp(input.endAt)}`,
    `SUMMARY:${icsText(input.title)}`,
    `URL:${input.url}`,
    `DESCRIPTION:${icsText(input.url)}`,
    "END:VEVENT",
    "END:VCALENDAR",
    "",
  ].join("\r\n");
}

/** The Google Calendar "add an event" address for the same event. */
export function googleCalendarHref(input: { title: string; startAt: string; endAt: string; url: string }): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: input.title,
    dates: `${calendarStamp(input.startAt)}/${calendarStamp(input.endAt)}`,
    details: input.url,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * `EX-CPG02`, Wave 30 decision 3: "Add to calendar" in a scheduled contest's
 * door, so a reader who registers on a Tuesday is reminded on the Saturday.
 * The file is built in the browser (no route, no tracking); Google Calendar
 * opens its own add page. Nothing when the window is not set.
 */
export function ContestCalendarLinks({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  if (!contest.startAt || !contest.endAt) return null;
  const title = locale === "bn" && contest.title_bn ? contest.title_bn : contest.title_en;
  const url = `${SITE_URL}/${locale}/contests/${contest.slug}`;
  const startAt = contest.startAt;
  const endAt = contest.endAt;

  function download() {
    const body = contestIcs({ slug: contest.slug, title, startAt, endAt, url, now: new Date() });
    const href = URL.createObjectURL(new Blob([body], { type: "text/calendar;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = href;
    link.download = `${contest.slug}.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
  }

  return (
    <div data-testid="contest-calendar" className="mt-3 flex flex-col gap-2">
      <Button type="button" variant="outlineDark" className="h-11 w-full" onClick={download}>
        <CalendarPlus className="h-4 w-4" aria-hidden />
        {t("calendarAdd")}
      </Button>
      <a
        href={googleCalendarHref({ title, startAt, endAt, url })}
        target="_blank"
        rel="noopener noreferrer"
        className="self-center text-[13px] font-medium text-neutral-400 underline-offset-2 transition-colors duration-200 hover:text-accent-peach hover:underline"
      >
        {t("calendarGoogle")}
      </a>
    </div>
  );
}
